// 261010 Epic 2 / P3e — thẻ "Diễn xuất" đầu bước Đạo diễn & diễn xuất: trạng thái cue, cờ lỗi mềm + kết quả pass Diễn xuất của máy tự chạy, nhãn sửa tay + nút "Dựng lại theo diễn xuất" MỘT video, thẻ luật (ruleCard), ngoại lệ tập (excPanel, chỉ đọc).
// Mọi quyết định ở `actingPolicy.ts` + `actingRebuild.ts` (thuần, có test); file này chỉ vẽ + nối API. KHÔNG import antd (antd v6 không load dưới jest — như thẻ phiếu AI chấm P3d). Không hỏi xác nhận, không hiện giá (AI chữ). Không đụng quyết định camera.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { filmApi } from '../../services/api/film';
import type { FilmEvent } from '../../types/film';
import type { ActingFlag, ActingOutcome, ActingState } from '../../types/filmActing';
import type { StyleProfileView } from '../../types/filmStyleProfile';
import { useAliveRef } from '../../hooks/useAliveRef';
import { ACTING_FIELD_LABEL, actingActionError, actingBarOf, actingFlagText, actingOutcomeOf, actingStatusOf, exceptionPanelOf, ruleCardOf } from './actingPolicy';
import { actingSourceOnlyOf } from './workflowFourActingBlockedUtils';
import { rebuildByActing, type RebuildPhase, type RebuildResult } from './actingRebuild';
import { JOBS_UNKNOWN_TEXT, type ActionError } from './workflowActionPolicy';
import { clearManualPlanOf, manualEditErrorText } from './workflowL6Utils';
import type { InteractiveProps } from './WorkflowStepShared';
import './acting.css';

interface Props extends Pick<InteractiveProps, 'planId' | 'jobs' | 'onActionDone' | 'onJobsRefresh'> {
  eventId: string;
  ev: FilmEvent;
  /** Cờ Diễn xuất của ĐÚNG video (trang tính từ `workflow-status.alerts`). */
  flags: ActingFlag[];
  /** `workflow-status.workflowRuns.stage2.actingOutcomes` (BE lượt 2); vắng ⇒ không hiện dòng kết quả chặng 2. */
  outcomes?: ActingOutcome[];
  /** Chu kỳ hỏi việc nền khi dựng lại (mặc định 2 giây); chỉ test hạ xuống. */
  pollMs?: number;
}

const TONE_CLASS: Record<string, string> = { ok: 'ok', warn: 'warn', doing: 'doing', muted: 'todo' };
const PHASE_LABEL: Record<RebuildPhase, string> = { ACTING: 'Đang viết diễn xuất…', DIRECTING: 'Đang dàn shot…' };
const RESULT_NOTE: Record<RebuildResult['kind'], string | null> = {
  DONE: 'Đã dựng lại shot của video theo diễn xuất.',
  ABORTED: null,
  ACTING_NOT_WRITTEN: 'AI chưa viết xong diễn xuất cho video này (xem Lịch sử) — giữ nguyên shot hiện có.',
  TIMEOUT: 'Quá 10 phút chưa xong — mở Lịch sử xem việc nền rồi bấm Tải lại.',
  JOBS_UNKNOWN: JOBS_UNKNOWN_TEXT,
};

const WorkflowActingPanel: React.FC<Props> = ({ planId, eventId, ev, jobs, flags, outcomes, pollMs, onActionDone, onJobsRefresh }) => {
  const alive = useAliveRef();
  const [style, setStyle] = useState<StyleProfileView | null>(null);
  const [styleErr, setStyleErr] = useState(false);
  const [state, setState] = useState<ActingState | null>(null);
  const [phase, setPhase] = useState<RebuildPhase | null>(null);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<ActionError | null>(null);
  const [okNote, setOkNote] = useState<string | null>(null);
  const [clearOpen, setClearOpen] = useState(false); // 261010 UI2 (L6c): xác nhận gỡ dấu sửa tay (không dùng antd ở file này)
  const [clearing, setClearing] = useState(false);

  // Hồ sơ phong cách chỉ để ĐỌC luật (không AI). Lỗi tải ⇒ thẻ luật nói rõ, các phần khác vẫn chạy.
  useEffect(() => {
    let cancelled = false;
    filmApi.getStyleProfile(planId).then((r) => { if (!cancelled && alive.current) setStyle(r); }).catch(() => { if (!cancelled && alive.current) setStyleErr(true); });
    return () => { cancelled = true; };
  }, [planId, alive]);

  // Trạng thái Diễn xuất của video (chỉ đọc, 0 AI): nạp lúc mở và mỗi lần trang tải lại chi tiết (`ev` đổi). Lỗi ⇒ im lặng, rơi về dữ liệu trong payload plan.
  useEffect(() => {
    let cancelled = false;
    filmApi.getActingState(planId, eventId).then((r) => { if (!cancelled && alive.current) setState(r); }).catch(() => { /* chỉ mất vài chi tiết (stale từ BE, sửa tay chưa kịp có marker) */ });
    return () => { cancelled = true; };
  }, [planId, eventId, ev, alive]);

  const status = useMemo(() => actingStatusOf(ev, style, state), [ev, style, state]);
  const bar = useMemo(() => actingBarOf({ isSky: true, eventId, ev, jobs, sending, state }), [eventId, ev, jobs, sending, state]);
  const rules = useMemo(() => ruleCardOf(style, ev.actingPlan), [style, ev.actingPlan]);
  const exc = exceptionPanelOf(ev.actingPlan);
  const outcome = useMemo(() => actingOutcomeOf(outcomes, eventId, flags), [outcomes, eventId, flags]);
  const button = bar.find((i): i is Extract<typeof i, { kind: 'button' }> => i.kind === 'button');

  const run = useCallback(async () => {
    if (!button) return;
    setErr(null); setOkNote(null); setSending(true);
    try {
      const r = await rebuildByActing(button.mode, {
        startActing: () => filmApi.startActing(planId, eventId),
        directActing: () => filmApi.directActing(planId, eventId),
        getState: () => filmApi.getActingState(planId, eventId),
        refreshJobs: async () => (await onJobsRefresh())?.jobs ?? null,
        sleep: (ms) => new Promise((res) => { setTimeout(res, ms); }),
        alive: () => alive.current,
        onPhase: (p) => { if (alive.current) setPhase(p); },
      }, pollMs ? { pollMs } : {});
      if (alive.current && RESULT_NOTE[r.kind]) setOkNote(RESULT_NOTE[r.kind]);
    } catch (e: unknown) { if (alive.current) setErr(actingActionError(e, 'Dựng lại theo diễn xuất')); }
    finally { if (alive.current) { setSending(false); setPhase(null); } onActionDone(); }
  }, [button, planId, eventId, onJobsRefresh, onActionDone, alive, pollMs]);

  // 261010 UI2 (L6c) — gỡ dấu "sửa tay" diễn xuất (giữ chữ) để lần Diễn xuất / Đạo diễn sau được ghi lại. CAS bằng `manualHash` của GET acting; chỉ AI chữ, không giá.
  const clearPlan = useMemo(() => clearManualPlanOf(state), [state]);
  const doClear = useCallback(async () => {
    if (!clearPlan.ok) return;
    setErr(null); setOkNote(null); setClearing(true);
    try {
      await filmApi.clearActingManual(planId, eventId, clearPlan.body);
      if (alive.current) { setOkNote('Đã gỡ dấu sửa tay — chữ giữ nguyên; lần chạy sau AI được ghi lại phần này.'); setClearOpen(false); }
    } catch (e: unknown) {
      if (alive.current) setErr({ kind: 'OTHER', text: manualEditErrorText(e, 'Gỡ dấu sửa tay'), reload: true });
    } finally {
      if (alive.current) setClearing(false);
      try { const r = await filmApi.getActingState(planId, eventId); if (alive.current) setState(r); } catch { /* giữ trạng thái cũ */ }
      onActionDone();
    }
  }, [clearPlan, planId, eventId, alive, onActionDone]);

  return (
    <section className="wf-act" aria-label="Diễn xuất" data-testid="wf-acting-panel">
      <div className="wf-act-h">
        <b>Diễn xuất</b>
        {status.chip && <span className={`wf-chip ${TONE_CLASS[status.chip.tone]}`} data-testid="wf-acting-chip">{status.chip.label}</span>}
        {actingSourceOnlyOf(state, ev) && <span className="wf-chip warn" data-testid="wf-acting-source-only" title="AI từ chối viết diễn xuất cho video này — máy dùng nguyên câu trong truyện">diễn xuất lấy từ nguồn</span>}
        <span className="wf-act-hint">AI chốt cue diễn xuất trước, Đạo diễn gắn cue vào shot — cách đặt máy vẫn do Đạo diễn quyết.</span>
      </div>
      {flags.map((f) => { const t = actingFlagText(f); return t ? <div key={f.code} className="wf-alert WARN" role="status" data-testid={`wf-acting-flag-${f.code}`}>{t}</div> : null; })}
      {outcome && <div className="wf-act-hint" data-testid="wf-acting-outcome">Máy tự chạy chặng 2 (lần gần nhất): <b>{outcome.label}</b></div>}
      {status.notes.map((n) => <div key={n} className="wf-act-hint" data-testid="wf-acting-note">{n}</div>)}
      {err && (
        <div className={`wf-alert ${err.kind === 'OTHER' ? 'ERROR' : 'WARN'}`} role="alert" data-testid="wf-action-error">{err.text}{' '}
          <button type="button" className="wf-btn" onClick={() => { setErr(null); onActionDone(); }}>Tải lại</button>
          <button type="button" className="wf-btn ghost" onClick={() => setErr(null)}>Đóng</button>
        </div>
      )}
      {okNote && <div className="wf-act-hint" role="status" data-testid="wf-acting-ok">{okNote}</div>}
      <div className="wf-act-bar">
        {bar.map((it) => (it.kind === 'manual-label'
          ? <span key="manual" className="wf-act-manual" data-testid="wf-acting-manual">{it.text}{it.fields.length ? ` · ${it.fields.map((f) => ACTING_FIELD_LABEL[f]).join(', ')}` : ''}</span>
          : <button key="btn" type="button" className="wf-btn primary" disabled={!it.enabled} title={it.enabled ? undefined : it.reason} onClick={() => { void run(); }} data-testid="wf-acting-run" data-mode={it.mode}>{sending ? (phase ? PHASE_LABEL[phase] : 'Đang gửi…') : it.label}</button>))}
        {button?.enabled && <span className="wf-act-hint">{button.mode === 'DIRECT_ONLY' ? 'Diễn xuất đã có và còn mới — chỉ dàn lại shot của video này theo nó.' : 'Viết lại diễn xuất rồi thay các shot của video này bằng bản mới.'} Chỉ dùng AI chữ.</span>}
      </div>
      {clearPlan.ok && (
        <div className="wf-act-bar" data-testid="wf-acting-clear-bar">
          {!clearOpen
            ? <button type="button" className="wf-btn" onClick={() => setClearOpen(true)} disabled={sending || clearing} data-testid="wf-acting-clear">Gỡ dấu sửa tay</button>
            : (
              <div className="wf-alert WARN" role="alertdialog" aria-label="Xác nhận gỡ dấu sửa tay" data-testid="wf-acting-clear-confirm">
                Gỡ dấu "sửa tay" của {clearPlan.shots} shot? Chữ đã sửa được giữ nguyên; lần chạy Diễn xuất / Đạo diễn sau, AI được ghi lại phần này.{' '}
                <button type="button" className="wf-btn primary" onClick={() => { void doClear(); }} disabled={clearing} data-testid="wf-acting-clear-ok">{clearing ? 'Đang gỡ…' : 'Xác nhận gỡ'}</button>
                <button type="button" className="wf-btn ghost" onClick={() => setClearOpen(false)} disabled={clearing}>Huỷ</button>
              </div>
            )}
        </div>
      )}
      {button && !button.enabled && button.reason && <div className="wf-act-hint" data-testid="wf-acting-why">{button.reason}</div>}

      <div className="wf-act-sec" data-testid="wf-acting-rules">
        <h4>Luật phong cách áp cho bước này</h4>
        {styleErr && !style
          ? <p className="muted" style={{ margin: 0 }}>Chưa tải được hồ sơ phong cách — mở lại trang để thử lại.</p>
          : rules.note && <p className="muted" style={{ margin: 0 }}>{rules.note}</p>}
        {rules.rules.length > 0 && (
          <ul className="wf-act-rules">{rules.rules.map((r) => (
            <li key={r.id}><b>{r.title}</b><span className="own">{r.ownerText}</span>{r.applied && <span className="app">đã áp</span>}
              <div className="muted">Khi {r.when} · thì {r.then}</div></li>))}
          </ul>
        )}
      </div>

      <div className="wf-act-sec" data-testid="wf-acting-exc">
        <h4>Ngoại lệ của tập này</h4>
        <p className="muted" style={{ margin: 0 }}>{exc.text} Chỗ sửa ngoại lệ tập chưa có ở bản này.</p>
      </div>
    </section>
  );
};

export default WorkflowActingPanel;
